<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '~/components/AppHeader.vue';
import AppBanner from '~/components/AppBanner.vue';
import AppRail from '~/components/AppRail.vue';
import TocOutline from '~/views/docs/components/TocOutline.vue';
import DocsMainNav from '~/views/docs/components/DocsMainNav.vue';
import { copyToClipboard } from '~/utilities/copy-to-clipboard';
import IconMenu from '~icons/fluent/list-16-regular';
import IconClose from '~icons/carbon/close';

const route = useRoute();
const isMobileOrTablet = useMediaQuery('(max-width: 1280px)');
const timeoutIdMap = new WeakMap();

const isMenuOpen = ref(false);

// Close the menu when the route changes.
watch(
  () => route.fullPath,
  () => (isMenuOpen.value = false),
);

// Disable body scrolling when the menu is open.
watch(isMenuOpen, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : '';
});

onMounted(() => {
  window.addEventListener('click', copyCodeToClipboard);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', copyCodeToClipboard);
});

/**
 * Copy the code block to the clipboard.
 *
 * This function is based on the `copyCode` function from VitePress.
 * @see https://github.com/vuejs/vitepress/blob/main/src/client/app/composables/copyCode.ts
 */
function copyCodeToClipboard(e: Event) {
  const el = e.target;

  if (el instanceof HTMLElement && el.matches('.md-it-codeblock > button.copy')) {
    const sibling = el.nextElementSibling?.nextElementSibling;

    if (!sibling) return;

    copyToClipboard(sibling.textContent || '').then(() => {
      el.classList.add('copied');
      clearTimeout(timeoutIdMap.get(el));
      const timeoutId = setTimeout(() => {
        el.classList.remove('copied');
        el.blur();
        timeoutIdMap.delete(el);
      }, 2000);
      timeoutIdMap.set(el, timeoutId);
    });
  }
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function openMenu() {
  isMenuOpen.value = true;
}

function closeMenu() {
  isMenuOpen.value = false;
}
</script>

<template>
  <div class="flex min-h-screen flex-nowrap">
    <AppRail />

    <div class="flex min-h-screen min-w-0 flex-auto flex-col items-stretch">
      <div
        class="sticky top-0 z-20 border-b border-new-gray-100/70 bg-white dark:border-new-gray-950/80 dark:bg-new-slate-700"
      >
        <AppBanner />

        <div class="container-spacing">
          <AppHeader>
            <template #leftColumn>
              <div class="text-lg font-semibold">PyScript Docs</div>
            </template>
          </AppHeader>
        </div>

        <div class="flex items-center justify-between px-4 py-2 xl:hidden">
          <button
            class="-m-1 flex items-center gap-2 rounded p-1 font-medium opacity-75 hover:bg-new-gray-50/50 hover:opacity-100"
            @click="openMenu"
          >
            <IconMenu class="shrink-0 text-base" />
            <span class="pr-1 text-sm">Menu</span>
          </button>

          <button class="-m-1 p-1 text-xs opacity-75 hover:opacity-100" @click="scrollToTop">
            Return to top
          </button>
        </div>
      </div>

      <div
        v-if="isMobileOrTablet && isMenuOpen"
        class="fixed inset-0 z-20 bg-black/60"
        @click="closeMenu"
      />

      <div class="relative flex min-h-screen bg-white/70 dark:bg-new-gray-950/60">
        <aside
          v-if="isMobileOrTablet && isMenuOpen"
          class="fixed left-0 top-0 z-30 h-screen w-72 overflow-auto bg-white px-4 py-10 dark:bg-new-gray-950 xl:hidden"
          style="scrollbar-width: thin"
        >
          <button class="absolute right-3 top-2" @click="closeMenu">
            <IconClose class="shrink-0 text-lg" />
          </button>
          <DocsMainNav class="text-sm" />
        </aside>

        <aside
          v-else
          class="sticky top-[65px] hidden h-[calc(100svh-65px)] w-72 shrink-0 overflow-auto border-r border-new-gray-100/70 bg-white px-3 py-4 dark:border-new-gray-900 dark:bg-new-gray-950/60 xl:block"
          style="scrollbar-width: thin"
        >
          <DocsMainNav class="text-sm" />
        </aside>

        <main
          id="main"
          class="max-w-full px-4 py-6 lg:flex lg:px-5 xl:mx-auto xl:min-w-0 xl:max-w-6xl xl:flex-auto xl:px-10"
        >
          <aside class="mb-4 w-full shrink-0 lg:order-2 lg:max-w-80 lg:pl-20">
            <div class="lg:sticky lg:top-28 xl:top-24">
              <!-- Key is needed to re-render the component on route change. -->
              <TocOutline :key="$route.path" />
            </div>
          </aside>

          <div class="prose min-w-0 flex-initial dark:prose-invert lg:order-1">
            <RouterView />
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
@import 'markdown-it-github-alerts/styles/github-colors-light.css';
@import 'markdown-it-github-alerts/styles/github-colors-dark-media.css';
@import 'markdown-it-github-alerts/styles/github-base.css';
</style>
